import api from './api';

export type CaixaStatus = 'Aberto' | 'Fechado';

export interface CaixaDTO {
  id: string;
  dataAbertura?: string;
  valorAbertura?: number;
  valorAberturaInformado?: number;
  saldoAtual?: number;
  valorFechamentoInformado?: number;
  status?: string;
  dataFechamento?: string;
  usuarioAberturaNome?: string;
  usuarioFechamentoNome?: string;
  observacoes?: string;
}

export interface PageResult<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  page: number;
  size: number;
}

function normalizeArrayPayload(data: any): any[] {
  const original = data;
  const content = ((original?.data ?? original)?.content ?? (Array.isArray(original?.data ?? original) ? (original?.data ?? original) : [])) as any[];
  return content || [];
}

function toCaixaDTO(x: any): CaixaDTO {
  return {
    id: String(x.id ?? x.uuid ?? ''),
    dataAbertura: x.dataAbertura ?? x.aberturaData ?? x.createdAt ?? x.data,
    // Preserva o valor informado na abertura quando disponível
    valorAberturaInformado: Number(x.valorAberturaInformado ?? x.valorInicial ?? x.valorAbertura ?? x.valor ?? 0),
    // Usa o informado como fonte primária para o valor inicial
    valorAbertura: Number(x.valorAberturaInformado ?? x.valorAbertura ?? x.valorInicial ?? x.valor ?? 0),
    saldoAtual: Number(x.saldoAtual ?? x.saldo ?? x.total ?? x.valorAtual ?? 0),
    valorFechamentoInformado: Number(x.valorFechamentoInformado ?? x.valorFechamento ?? 0),
    status: x.status ?? x.situacao ?? (x.dataFechamento ? 'Fechado' : 'Aberto'),
    dataFechamento: x.dataFechamento ?? x.fechamentoData ?? x.closedAt,
    usuarioAberturaNome: x.usuarioAberturaNome ?? x.usuarioAbertura ?? x.abertoPor,
    usuarioFechamentoNome: x.usuarioFechamentoNome ?? x.usuarioFechamento ?? x.fechadoPor,
    observacoes: x.observacoes ?? x.obs ?? x.descricao,
  };
}

export async function listCaixas(params: {
  page?: number;
  size?: number;
  sort?: string; // e.g. "dataAbertura,desc"
  id?: string;
  usuario?: string;
  status?: CaixaStatus | 'Todos';
}): Promise<PageResult<CaixaDTO>> {
  const { page = 0, size = 10, sort, id, usuario, status } = params || {};
  const resp = await api.get('/v1/caixas', { params: { page, size, sort, id, usuario, status: status === 'Todos' ? undefined : status } });

  // Paginação padrão (Page<T>)
  const payload: any = resp.data?.data ?? resp.data ?? {};
  const isPage = payload && typeof payload === 'object' && Array.isArray(payload.content);

  if (isPage) {
    const mapped = (payload.content || []).map(toCaixaDTO);
    const totalElements = payload.totalElements ?? payload.total ?? mapped.length;
    const sizeUsed = payload.size ?? payload.pageable?.pageSize ?? size;
    const pageUsed = payload.page ?? payload.pageable?.pageNumber ?? page;
    const totalPages = payload.totalPages ?? Math.max(1, Math.ceil(totalElements / sizeUsed));
    return { content: mapped, totalElements, totalPages, page: pageUsed, size: sizeUsed };
  }

  // Fallback para array simples
  const arr = normalizeArrayPayload(resp.data).map(toCaixaDTO);
  const totalElements = arr.length;
  const totalPages = Math.max(1, Math.ceil(totalElements / size));
  return { content: arr.slice(page * size, page * size + size), totalElements, totalPages, page, size };
}

export async function abrirCaixa(input: { valorAbertura: number; dataAbertura?: string; observacoes?: string; }) {
  const body: any = {
    valorAbertura: input.valorAbertura,
    dataAbertura: input.dataAbertura ?? new Date().toISOString().slice(0, 10),
    observacoes: input.observacoes ?? `Abertura automática de caixa (${new Date().toLocaleDateString('pt-BR')})`,
  };
  body.valorInicial = body.valorInicial ?? body.valorAbertura;
  return api.post('/v1/caixas', body);
}

export async function getCaixaById(id: string): Promise<CaixaDTO> {
  const resp = await api.get(`/v1/caixas/${encodeURIComponent(id)}`);
  const payload = resp.data?.data ?? resp.data;
  return toCaixaDTO(payload);
}

export async function getCaixaUltimoAberto(): Promise<CaixaDTO> {
  const resp = await api.get(`/v1/caixas/ultimo-aberto`);
  const payload = resp.data?.data ?? resp.data;
  return toCaixaDTO(payload);
}

export async function fecharCaixa(id: string | number, input?: { dataFechamento?: string }) {
  const body: any = {
    dataFechamento: input?.dataFechamento ?? new Date().toISOString().slice(0, 10),
    status: 'Fechado',
  };
  return api.put(`/v1/caixas/${encodeURIComponent(String(id))}`, body);
}